import { typedMarkingVatOptions } from '@/features/analytics/invite-links/const/marking'
import { useCreateAgencyContract, useUpdateAgencyContract } from '@/shared/api'
import {
    Button,
    Checkbox,
    DatePickerWithIcon,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
    SelectDropdown,
} from '@/shared/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { VkOrdAgencyContract } from '@synapex/prisma/client'
import { WorkspaceDescriptor } from '@synapex/shared2/entities'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import z from 'zod'
import { toUTCDate } from '@/shared/lib/date'

const agencyContractSchema = z.object({
    number: z.string().min(1, 'Номер договора обязателен'),

    vatPercent: z
        .string()
        .refine(
            (val) => val === '' || !isNaN(parseInt(val)),
            'Введите корректное число'
        )
        .refine(
            (val) => val === '' || (parseInt(val) >= -1 && parseInt(val) <= 22),
            'НДС должен быть от 0 до 22%'
        ),
    startDate: z.date({
        message: 'Дата начала обязательна',
    }),

    type: z.enum(['service', 'mediation']),
})

type AgencyContractFormData = z.infer<typeof agencyContractSchema>

type CreateAgencyContractDialogProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
    editingContract: VkOrdAgencyContract | null
} & WorkspaceDescriptor

const CreateAgencyContractDialog = ({
    open,
    onOpenChange,
    editingContract,
    ...props
}: CreateAgencyContractDialogProps) => {
    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        setValue,
        watch,
    } = useForm<AgencyContractFormData>({
        resolver: zodResolver(agencyContractSchema),
        defaultValues: {
            number: '',
            vatPercent: '-1',
            startDate: toUTCDate(new Date()),
            type: 'mediation' as const,
        },
    })

    const createAgencyContract = useCreateAgencyContract(props)
    const updateAgencyContract = useUpdateAgencyContract(props)

    const isEditing = !!editingContract
    const isLoading =
        createAgencyContract.isPending || updateAgencyContract.isPending

    useEffect(() => {
        if (editingContract) {
            setValue('number', editingContract.number)
            setValue('vatPercent', editingContract.vat_percent.toString())
            setValue('startDate', toUTCDate(new Date(editingContract.start_date)))

            setValue('type', editingContract.type as 'service' | 'mediation')
        } else {
            reset()
        }
    }, [editingContract, setValue, reset])

    const onSubmit = async (data: AgencyContractFormData) => {
        try {
            const vatPercent =
                data.vatPercent === '' ? 0 : parseFloat(data.vatPercent)

            const startDate = new Date(
                Date.UTC(
                    data.startDate.getFullYear(),
                    data.startDate.getMonth(),
                    data.startDate.getDate()
                )
            )

            if (isEditing) {
                await updateAgencyContract.mutateAsync({
                    contractId: editingContract.vk_ord_agency_contract_id,
                    contract: {
                        number: data.number,
                        vat_percent: vatPercent,
                        start_date: startDate,
                        type: data.type,
                        is_framework_contract: true,
                    },
                })
                toast.success('Договор успешно обновлен')
            } else {
                await createAgencyContract.mutateAsync({
                    number: data.number,
                    vat_percent: vatPercent,
                    start_date: startDate,
                    type: data.type,
                    is_framework_contract: true,
                })
                toast.success('Договор успешно создан')
            }
            onOpenChange(false)
            reset()
        } catch (error) {
            toast.error('Произошла ошибка при сохранении договора')
            console.error(error)
        }
    }

    const handleCancel = () => {
        onOpenChange(false)
        reset()
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
            <DialogContent
                className="max-w-md bg-white"
                aria-describedby={undefined}
            >
                <DialogHeader>
                    <DialogTitle>
                        {isEditing
                            ? 'Редактировать договор'
                            : 'Добавить новый договор'}
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    <div>
                        <Label className="mb-2 block text-sm font-medium">
                            Тип договора
                        </Label>

                        <div className="flex flex-col gap-2">
                            <div className="flex gap-2">
                                <div className="flex">
                                    <Checkbox
                                        value="service"
                                        id="service"
                                        name="type"
                                        checked={watch('type') === 'service'}
                                        onChange={(e) => {
                                            setValue(
                                                'type',
                                                e.target.value as 'service'
                                            )
                                        }}
                                    />
                                    <Label
                                        htmlFor="service"
                                        className="ml-2 text-xs"
                                    >
                                        Договор оказания услуг
                                    </Label>
                                </div>
                                <div className="flex">
                                    <Checkbox
                                        value="mediation"
                                        id="mediation"
                                        name="type"
                                        checked={watch('type') === 'mediation'}
                                        onChange={(e) => {
                                            setValue(
                                                'type',
                                                e.target.value as 'mediation'
                                            )
                                        }}
                                    />
                                    <Label
                                        htmlFor="mediation"
                                        className="ml-2 text-xs"
                                    >
                                        Посреднический договор
                                    </Label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Label className="mb-2 block text-sm font-medium">
                            Введите номер
                        </Label>
                        <Controller
                            name="number"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    placeholder="Номер договора"
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={errors.number?.message}
                                />
                            )}
                        />
                    </div>

                    <div>
                        <Label className="mb-2 block text-sm font-medium">
                            Ставка НДС
                        </Label>
                        <div className="flex gap-2">
                            <div className="w-24">
                                <Controller
                                    name="vatPercent"
                                    control={control}
                                    render={({ field }) => (
                                        <SelectDropdown
                                            options={typedMarkingVatOptions}
                                            onSelect={(v) =>
                                                field.onChange(v.value)
                                            }
                                            value={field.value}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <Label className="mb-2 block text-sm font-medium">
                            Дата начала
                        </Label>
                        <Controller
                            name="startDate"
                            control={control}
                            render={({ field }) => (
                                <DatePickerWithIcon
                                    selected={field.value}
                                    onChange={(date: Date | undefined) => {
                                        field.onChange(
                                            toUTCDate(date || new Date())
                                        )
                                    }}
                                    dateFormat="dd.MM.yy"
                                    dropdownMode="scroll"
                                    className="text-xs"
                                    maxDate={new Date()}
                                />
                            )}
                        />
                        {errors.startDate && (
                            <p className="mt-1 text-xs text-red-500">
                                {errors.startDate.message}
                            </p>
                        )}
                    </div>

                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleCancel}
                            disabled={isLoading || isSubmitting}
                            className="flex-1"
                        >
                            Отмена
                        </Button>
                        <Button
                            type="submit"
                            disabled={isLoading || isSubmitting}
                            className="flex-1"
                        >
                            {isLoading || isSubmitting
                                ? 'Сохранение...'
                                : 'Сохранить'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    )
}

const CreateAgencyContractDialogWrapper: React.FC<
    CreateAgencyContractDialogProps
> = ({ open, ...props }) => {
    if (!open) {
        return null
    }

    return <CreateAgencyContractDialog {...props} open={open} />
}

export default CreateAgencyContractDialogWrapper
